import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PersistMessageReferenceResponseModel } from '../models/persist-message-reference-response-model';
import { PersistMessageReferenceService } from '../persist-message-reference.service';

@Component({
    selector: 'view-messageref',
    standalone: true,
    imports: [CommonModule],
    providers: [PersistMessageReferenceService],
    templateUrl: './view-messageref.component.html'
})
export class ViewMessageRefComponent implements OnInit {
  @Input() row: PersistMessageReferenceResponseModel;

  public jsonData: any = null;
  public loading: boolean = true;
  public error: string = '';

  constructor(
    public activeModal: NgbActiveModal,
    private sanitizer: DomSanitizer,
    private persistMessageReferenceService: PersistMessageReferenceService
  ) {}

  ngOnInit(): void {
    this.persistMessageReferenceService.getPersistMessageContent(this.row.referenceId).subscribe({
      next: (result) => {
        this.jsonData = result;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.message || 'Failed to load message content';
        console.error('Error loading message content:', err);
      }
    });
  }

  get isXml(): boolean {
    const raw = typeof this.jsonData === 'string' ? this.jsonData.trimStart() : '';
    return raw.startsWith('<');
  }

  get isJson(): boolean {
    if (this.isXml) return false;
    try {
      const raw = typeof this.jsonData === 'string' ? this.jsonData : null;
      if (raw !== null) JSON.parse(raw);
      return true;
    } catch {
      return false;
    }
  }

  get formattedContent(): string {
    if (this.isXml) {
      return this.formatXml(typeof this.jsonData === 'string' ? this.jsonData : String(this.jsonData));
    }
    try {
      const parsed = typeof this.jsonData === 'string' ? JSON.parse(this.jsonData) : this.jsonData;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return typeof this.jsonData === 'string' ? this.jsonData : JSON.stringify(this.jsonData, null, 2);
    }
  }

  private formatXml(xml: string): string {
    let formatted = '';
    let indent = 0;
    const tab = '  ';
    xml = xml.replace(/\r\n|\r/g, '\n').trim();
    const nodes = xml.split(/(<[^>]+>)/).map(n => n.trim()).filter(n => n !== '');
    let i = 0;
    while (i < nodes.length) {
      const node = nodes[i];
      if (/^<\//.test(node)) {
        indent--;
        formatted += tab.repeat(Math.max(indent, 0)) + node + '\n';
        i++;
      } else if (/^<[^?!][^>]*[^/]>$/.test(node) || /^<[^?!/][^>]*>$/.test(node)) {
        const nextNode = nodes[i + 1];
        const afterNext = nodes[i + 2];
        if (nextNode !== undefined && !/^</.test(nextNode) && afterNext !== undefined && /^<\//.test(afterNext)) {
          // Inline simple text content: <Tag>value</Tag>
          formatted += tab.repeat(indent) + node + nextNode + afterNext + '\n';
          i += 3;
        } else {
          formatted += tab.repeat(indent) + node + '\n';
          indent++;
          i++;
        }
      } else {
        formatted += tab.repeat(indent) + node + '\n';
        i++;
      }
    }
    return formatted.trimEnd();
  }

  get syntaxHighlightedContent(): SafeHtml {
    if (this.isXml) {
      return this.highlightXml(this.formattedContent);
    }
    if (this.isJson) {
      return this.highlightJson(this.formattedContent);
    }
    const escaped = this.formattedContent
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return this.sanitizer.bypassSecurityTrustHtml(`<span class="plain-text">${escaped}</span>`);
  }

  private highlightJson(json: string): SafeHtml {
    let html = json
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    html = html.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, (match) => {
      let cls = 'json-number';
      if (/^"/.test(match)) {
        if (/:$/.test(match)) {
          cls = 'json-key';
        } else {
          cls = 'json-string';
        }
      } else if (/true|false/.test(match)) {
        cls = 'json-boolean';
      } else if (/null/.test(match)) {
        cls = 'json-null';
      }
      return '<span class="' + cls + '">' + match + '</span>';
    });

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private highlightXml(xml: string): SafeHtml {
    let html = xml
      .replace(/&/g, '&amp;')
      .replace(/</g, '\x00LT\x00')
      .replace(/>/g, '\x00GT\x00');

    // Processing instructions / declarations: <?...?>
    html = html.replace(
      /\x00LT\x00(\?)([^\x00]*?)(\?)\x00GT\x00/g,
      (_, open, inner, close) => {
        // Split PI target name from the rest (e.g. "xml" from " version=\"1.0\" ...")
        const piMatch = inner.match(/^([A-Za-z][A-Za-z0-9_:\-.]*)(.*)$/s);
        const piTarget = piMatch ? `<span class="xml-element">${piMatch[1]}</span>` : '';
        const piRest = piMatch ? piMatch[2] : inner;
        const attrHtml = piRest.replace(
          /([A-Za-z][A-Za-z0-9_:\-.]*)=("([^"]*)"|'([^']*)')/g,
          '<span class="xml-pi-attr-name">$1</span>=<span class="xml-attr-value">$2</span>'
        );
        return `<span class="xml-pi-delim">&lt;${open}</span>${piTarget}<span class="xml-decl">${attrHtml}</span><span class="xml-pi-delim">${close}&gt;</span>`;
      }
    );
    // Comments: <!--...-->
    html = html.replace(
      /\x00LT\x00(!--[\s\S]*?--)\x00GT\x00/g,
      '<span class="xml-comment">&lt;$1&gt;</span>'
    );
    // Closing tags: </tagName>
    html = html.replace(
      /\x00LT\x00(\/[A-Za-z][A-Za-z0-9_:\-.]*)\x00GT\x00/g,
      (_, tag) => `<span class="xml-bracket">&lt;</span><span class="xml-element">${tag}</span><span class="xml-bracket">&gt;</span>`
    );
    // Opening / self-closing tags with optional attributes
    html = html.replace(
      /\x00LT\x00([A-Za-z][A-Za-z0-9_:\-.]*)([^\x00]*?)(\/?)\x00GT\x00/g,
      (_, name, attrs, selfClose) => {
        const attrHtml = attrs.replace(
          /([A-Za-z][A-Za-z0-9_:\-.]*)=("([^"]*)"|'([^']*)')/g,
          '<span class="xml-attr-name">$1</span>=<span class="xml-attr-value">$2</span>'
        );
        return `<span class="xml-bracket">&lt;</span><span class="xml-element">${name}</span>${attrHtml}<span class="xml-bracket">${selfClose}&gt;</span>`;
      }
    );
    // Restore any remaining placeholders (plain text nodes)
    html = html.replace(/\x00LT\x00/g, '&lt;').replace(/\x00GT\x00/g, '&gt;');

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  copyToClipboard(): void {
    navigator.clipboard.writeText(this.formattedContent).then(() => {
      console.log('Content copied to clipboard');
    });
  }
}
